import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

declare global {
  interface Window {
    OneSignalDeferred?: Array<(os: any) => void | Promise<void>>;
    OneSignal?: any;
  }
}

/**
 * Integração do OneSignal Web (push no navegador).
 *
 * O SDK (OneSignalSDK.page.js) é carregado com `defer` no index.html. Toda
 * operação passa pela fila oficial `window.OneSignalDeferred`, que só roda após
 * o SDK estar disponível.
 *
 * IMPORTANTE — ordenação: `init()` e `login()` NÃO podem ir como callbacks
 * separados no OneSignalDeferred. A fila dispara os callbacks assim que o SDK
 * carrega, sem esperar o `await OneSignal.init()` do primeiro terminar; o
 * `login()` então rodava num SDK meio-inicializado (usuário anônimo ainda
 * inexistente) e estourava um TypeError interno no LoginManager
 * ("Cannot read properties of undefined"). A correção guarda a promise do init
 * (`ready`) e faz login/logout/permissão SEMPRE aguardarem-na — garantindo que
 * o SDK esteja pronto antes de qualquer operação de identidade.
 */
@Injectable({ providedIn: 'root' })
export class OneSignalWebService {

  private initialized = false;
  /** Resolve com a instância do OneSignal somente após o init concluir. */
  private ready?: Promise<any>;

  /** Enfileira uma operação e devolve uma promise com seu resultado. */
  private enqueue<T>(op: (os: any) => T | Promise<T>): Promise<T> {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    return new Promise<T>((resolve, reject) => {
      window.OneSignalDeferred!.push(async (OneSignal: any) => {
        try {
          resolve(await op(OneSignal));
        } catch (e) {
          reject(e);
        }
      });
    });
  }

  init(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.ready = this.enqueue(async (OneSignal) => {
      await OneSignal.init({
        appId: environment.onesignal.appId,
        notifyButton: { enable: false },
        allowLocalhostAsSecureOrigin: !environment.production,
      });
      return OneSignal;
    });
  }

  /** Vincula o External ID (userId) — só executa após o init estar pronto. */
  async registerUser(userId: string): Promise<void> {
    if (!userId || !this.ready) return;
    try {
      const OneSignal = await this.ready;
      await OneSignal.login(userId);
    } catch { /* conflito de alias ou estado transitório: ignora */ }
  }

  async unregisterUser(): Promise<void> {
    if (!this.ready) return;
    try {
      const OneSignal = await this.ready;
      await OneSignal.logout();
    } catch { /* sem sessão ativa: ignora */ }
  }

  async requestPermission(): Promise<void> {
    if (!this.ready) return;
    try {
      const OneSignal = await this.ready;
      await OneSignal.Notifications.requestPermission();
    } catch { /* usuário pode recusar */ }
  }

  get hasPermission(): boolean {
    return !!window.OneSignal?.Notifications?.permission;
  }
}
